import { useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { COLOR_SUITS, MISSIONS, missionRuleDescriptions, type Suit } from '@crew/shared';
import { SuitIcon } from './PlayingCard.js';

type Chapter = 'goal' | 'trick' | 'tasks' | 'comms' | 'missions';

const CHAPTERS: { id: Chapter; label: string }[] = [
  { id: 'goal', label: 'The goal' },
  { id: 'trick', label: 'Playing a trick' },
  { id: 'tasks', label: 'Tasks' },
  { id: 'comms', label: 'Comms' },
  { id: 'missions', label: 'Modifiers' }
];

export function Manual({ onClose }: { onClose: () => void }) {
  const [chapter, setChapter] = useState<Chapter>('goal');
  const panelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const focusables = () =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) ?? []
      );

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      // Keep focus inside the dialog while it is open.
      const items = focusables();
      if (items.length === 0) return;
      const first = items[0] as HTMLElement;
      const last = items[items.length - 1] as HTMLElement;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || active === panelRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKey, true);
    panelRef.current?.focus();
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const onTabKey = (e: ReactKeyboardEvent, index: number) => {
    if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
    e.preventDefault();
    const next =
      e.key === 'ArrowRight'
        ? (index + 1) % CHAPTERS.length
        : (index - 1 + CHAPTERS.length) % CHAPTERS.length;
    const target = CHAPTERS[next] as { id: Chapter };
    setChapter(target.id);
    panelRef.current?.querySelector<HTMLElement>(`#tab-${target.id}`)?.focus();
  };

  return (
    <div className="manual-backdrop" onClick={onClose}>
      <div
        className="manual"
        role="dialog"
        aria-modal="true"
        aria-label="Mission manual"
        tabIndex={-1}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="manual__head">
          <div>
            <p className="manual__eyebrow">Crew: Quest for Planet Nine</p>
            <h2>Mission Manual</h2>
          </div>
          <button className="btn btn--ghost btn--small" onClick={onClose} aria-label="Close manual">
            Close
          </button>
        </header>

        <div className="manual__tabs" role="tablist" aria-label="Manual chapters">
          {CHAPTERS.map((c, i) => (
            <button
              key={c.id}
              id={`tab-${c.id}`}
              role="tab"
              aria-selected={chapter === c.id}
              aria-controls={`panel-${c.id}`}
              tabIndex={chapter === c.id ? 0 : -1}
              className={chapter === c.id ? 'is-active' : ''}
              onClick={() => setChapter(c.id)}
              onKeyDown={(e) => onTabKey(e, i)}
            >
              {c.label}
            </button>
          ))}
        </div>

        <div
          className="manual__body"
          role="tabpanel"
          id={`panel-${chapter}`}
          aria-labelledby={`tab-${chapter}`}
          tabIndex={0}
        >
          {chapter === 'goal' ? <Goal /> : null}
          {chapter === 'trick' ? <Trick /> : null}
          {chapter === 'tasks' ? <Tasks /> : null}
          {chapter === 'comms' ? <Comms /> : null}
          {chapter === 'missions' ? <Modifiers /> : null}
        </div>
      </div>
    </div>
  );
}

function Goal() {
  return (
    <>
      <p className="manual__lead">
        Somewhere beyond Neptune sits a ninth planet nobody has laid eyes on. You and your crew are
        flying out to find it. There are no winners and losers at this table. Either the whole
        crew completes the mission, or the whole crew fails it.
      </p>
      <h3>What you are trying to do</h3>
      <p>
        Every mission deals out a handful of <strong>tasks</strong>. A task is a single colour card,
        and the crew member who claimed it must personally win the trick that card is played in.
        Complete every task and satisfy all mission-specific rules to succeed.
      </p>
      <h3>How you lose</h3>
      <ul>
        <li>Somebody wins a trick containing a task card that belongs to another player.</li>
        <li>The crew runs out of cards with tasks still open.</li>
        <li>A mission-specific rule is broken. See Modifiers.</li>
      </ul>
      <h3>The hard part</h3>
      <p>
        You may not tell anyone what is in your hand. No hinting in chat, no "I can take a blue".
        You get exactly one formal hint for the entire mission, and everything else has to be read
        from what people choose to play. That constraint is the game.
      </p>
      <div className="manual__deck">
        {COLOR_SUITS.map((suit) => (
          <span key={suit} className={`manual__suit suit-${suit}`}>
            <SuitIcon suit={suit as Suit} className="manual__suit-icon" />
            {suit} 1&ndash;9
          </span>
        ))}
        <span className="manual__suit suit-rocket">
          <SuitIcon suit="rocket" className="manual__suit-icon" />
          rocket 1&ndash;4
        </span>
      </div>
    </>
  );
}

function Trick() {
  return (
    <>
      <p className="manual__lead">
        All 40 cards are dealt out. Whoever holds the <strong>rocket 4</strong> is the commander and
        leads the first trick.
      </p>
      <ol className="manual__steps">
        <li>
          <strong>The leader plays any card.</strong> The colour they play is the led colour for
          that trick.
        </li>
        <li>
          <strong>Everyone else must follow that colour</strong> if they hold it. Cards you are not
          allowed to play are greyed out, so you cannot break this rule by accident.
        </li>
        <li>
          <strong>If you are void</strong> in the led colour, play anything, including a
          rocket.
        </li>
        <li>
          <strong>Highest card wins.</strong> Rockets beat every colour, so the highest rocket takes
          the trick. With no rockets, the highest card of the led colour wins.
        </li>
        <li>
          <strong>The winner leads the next trick.</strong>
        </li>
      </ol>
      <p className="manual__note">
        With three players the deal is uneven, so one seat holds an extra card and the last trick has
        just one card in it. That is normal.
      </p>
    </>
  );
}

function Tasks() {
  return (
    <>
      <p className="manual__lead">
        Before the first trick the crew claims the tasks, starting with the commander. On your turn
        take as many as you like. Click one of your claimed tasks again to release it while it is
        still your draft turn. Claiming the last unclaimed task starts play immediately, so it cannot
        be undone. Otherwise, end your turn when ready. The draft goes around once, and whatever
        nobody wanted lands on the commander. Choose carefully: you are promising to win that exact
        card.
      </p>
      <h3>Badges on a task</h3>
      <table className="manual__table">
        <thead>
          <tr>
            <th>Badge</th>
            <th>What it means</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <span className="badge-demo">none</span>
            </td>
            <td>Complete it whenever you like.</td>
          </tr>
          <tr>
            <td>
              <span className="badge-demo">#1 #2 #3</span>
            </td>
            <td>
              Sequenced. All numbered tasks must be completed in ascending order. Taking
              <strong> #2</strong> while <strong>#1</strong> is still open fails the mission.
            </td>
          </tr>
          <tr>
            <td>
              <span className="badge-demo">last</span>
            </td>
            <td>Must be the very final task completed. Take it early and the mission fails.</td>
          </tr>
        </tbody>
      </table>
      <p className="manual__note">
        If one trick completes several tasks at once, the game resolves them in the only order that
        could be legal: sequenced tasks in order, then unconstrained ones, then the final task.
      </p>
    </>
  );
}

function Comms() {
  return (
    <>
      <p className="manual__lead">
        One hint token per player, per mission. Press <strong>Give a hint</strong>, then pick a card
        to place face up in front of you. Hints only go down between tricks, never once cards are on
        the table.
      </p>
      <h3>What a hint can say</h3>
      <ul className="manual__hints">
        <li>
          <span className="hint-chip hint-chip--only">only</span> this is my only card of that
          colour
        </li>
        <li>
          <span className="hint-chip hint-chip--high">highest</span> this is my highest card of that
          colour
        </li>
        <li>
          <span className="hint-chip hint-chip--low">lowest</span> this is my lowest card of that
          colour
        </li>
      </ul>
      <p>
        The game will only let you place a hint that is actually true, so a hint is always
        trustworthy. You cannot hint a rocket. Once you play the hinted card, its active hint
        disappears, but your token stays used for the rest of the mission.
      </p>
      <p className="manual__note">
        Talk all you like about the plan: who should take which task, what order to go in.
        Just never describe the cards in your hand.
      </p>
    </>
  );
}

function Modifiers() {
  const finalMission = MISSIONS[MISSIONS.length - 1]!;
  return (
    <>
      <p className="manual__lead">
        Later missions layer on extra constraints. The mission brief always tells you which are
        active.
      </p>
      <table className="manual__table">
        <tbody>
          <tr>
            <td>Radio silence</td>
            <td>Hint tokens are disabled completely.</td>
          </tr>
          <tr>
            <td>Comms locked</td>
            <td>No hints until a given trick number.</td>
          </tr>
          <tr>
            <td>Comms window</td>
            <td>Hints must be placed before a given trick.</td>
          </tr>
          <tr>
            <td>Commander opens</td>
            <td>The commander has to win the first trick.</td>
          </tr>
          <tr>
            <td>No repeats</td>
            <td>Nobody may win two tricks in a row.</td>
          </tr>
          <tr>
            <td>Commander sits out</td>
            <td>The commander must not win any tricks.</td>
          </tr>
          <tr>
            <td>Everyone scores</td>
            <td>Every pilot must win a trick, even after all tasks are complete. These missions cap at five players.</td>
          </tr>
          <tr>
            <td>Trick cap</td>
            <td>No pilot may exceed the mission's maximum number of tricks.</td>
          </tr>
        </tbody>
      </table>
      <h3>The campaign</h3>
      <p>
        {MISSIONS.length} missions ship with the game, roughly ordered by difficulty. Mission{' '}
        {finalMission.id}, {finalMission.name}, combines these rules:
      </p>
      <ul>
        {missionRuleDescriptions(finalMission).map((rule) => <li key={rule}>{rule}</li>)}
      </ul>
      <p>Losing is normal. Retry from the results screen and the deal is reshuffled.</p>
    </>
  );
}
